import * as React from 'react';
import {
  Filter,
  FilterChangeEvent,
  Operators,
} from '@progress/kendo-react-data-tools';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import {
  saveNodesConfigFilters,
  saveNodesJsonConfigFilters,
} from '../../../store/UserSettings/UserSettingsActions';
import { ASSET } from '../../../utils/constants';
import { GetString } from '../../StringResourceManager';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../../AlertsGrid/GridLocale';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import { CustomFilterNodeConfigInfo } from './CustomFilterNodeConfigInfo';
import DynamicComboBox from '../../DynamicComboBox';
import { ApiEnums } from '../../../Interfaces/ApiEnums';

const useStyles = makeStyles(() =>
  createStyles({
    filterArea: {
      Height: '100%',
      width: '100%',
      overflow: 'auto',
    },
  }),
);

interface Props {
  configSettingType: ApiEnums.ConfigSettingsType;
}

const NodeConfigFilter: React.FC<Props> = ({
  configSettingType,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const configFilter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.nodesConfigFilters,
  );

  const jsonConfigFilter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.nodesJsonConfigFilters,
  );

  const onFilterChange = (event: FilterChangeEvent) => {
    if (configSettingType === ApiEnums.ConfigSettingsType.RegistrySetting)
      saveNodesConfigFilters(dispatch, event.filter);
    else saveNodesJsonConfigFilters(dispatch, event.filter);
  };

  const siteNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumbers,
  );

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.divisions,
  );

  const nodeTypes: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.nodeTypes,
  );

  const clusters: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.clusters,
  );

  const laneNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.laneNumbers,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const filterMap = {
    [ApiEnums.ConfigSettingsType.RegistrySetting]: configFilter,
    [ApiEnums.ConfigSettingsType.JsonSetting]: jsonConfigFilter,
  };

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const CustomSiteNumberFilter = (props: any) => {
    const onChangeSiteNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeSiteNumber}
        data={siteNumbers}
        value={props.filter.value}
        placeholder={GetString('STORE_PLACEHOLDER')}
      />
    );
  };

  const CustomClusterFilter = (props: any) => {
    const onChangeCluster = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeCluster}
        data={clusters}
        value={props.filter.value}
        placeholder={GetString('CLUSTER_PLACEHOLDER')}
      />
    );
  };

  const CustomLaneNumberFilter = (props: any) => {
    const onChangeLaneNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneNumber}
        data={laneNumbers}
        value={props.filter.value}
        placeholder={GetString('LANE_PLACEHOLDER')}
      />
    );
  };

  const CustomNodeTypeFilter = (props: any) => {
    const onChangeNodeType = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeNodeType}
        data={nodeTypes}
        value={props.filter.value}
        placeholder={GetString('NODE_TYPE_PLACEHOLDER')}
      />
    );
  };

  const CustomDivisionFilter = (props: any) => {
    const onChangeDivision = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDivision}
        data={divisions}
        value={props.filter.value}
        placeholder={GetString('DIVISION_PLACEHOLDER')}
      />
    );
  };

  const CustomFilterComponent = React.useCallback(
    (props: any) => (
      <CustomFilterNodeConfigInfo
        {...props}
        configSettingType={configSettingType}
      />
    ),
    [configSettingType],
  );

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <Filter
          value={filterMap[configSettingType]}
          className={classes.filterArea}
          onChange={onFilterChange}
          fields={[
            {
              name: ASSET.FIELD_CONFIG_SETTING,
              label: GetString('SETTING'),
              filter: CustomFilterComponent,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_STORE_NAME,
              label: GetString('FIELD_STORE_LABEL'),
              filter: CustomSiteNumberFilter,
              operators: Operators.text.filter(e => e.operator === 'eq'),
            },
            {
              name: ASSET.FIELD_CLUSTER_NAME,
              label: GetString('FIELD_CLUSTER_LABEL'),
              filter: CustomClusterFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_LANE_NAME,
              label: GetString('FIELD_LANE_LABEL'),
              filter: CustomLaneNumberFilter,
              operators: Operators.text,
            },
            {
              name: ASSET.FIELD_DIVISION_NAME,
              label: GetString('FIELD_DIVISION_LABEL'),
              filter: CustomDivisionFilter,
              operators: Operators.text.filter(e => e.operator === 'eq'),
            },
            {
              name: ASSET.FIELD_NODETYPE_NAME,
              label: GetString('FIELD_NODETYPE_LABEL'),
              filter: CustomNodeTypeFilter,
              operators: Operators.text.filter(e => e.operator === 'eq'),
            },
          ]}
        />
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default NodeConfigFilter;
